import * as React from "react"
import * as ReactDOM from "react-dom"
import { cn } from "@/lib/utils"
import { X } from "lucide-react"

type SnackbarVariant = "default" | "success" | "error" | "warning" | "info"

const snackbarBaseClassName =
    "fixed bottom-6 left-1/2 z-[1400] -translate-x-1/2 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg backdrop-blur-md transition-all duration-300 ease-out max-w-lg"

const snackbarVariantClasses: Record<SnackbarVariant, string> = {
    default: "bg-[color-mix(in_srgb,var(--text-primary)_80%,transparent)] text-[var(--text-inverse)]",
    success: "bg-[color-mix(in_srgb,var(--success)_80%,transparent)] text-[var(--text-inverse)]",
    error: "bg-[color-mix(in_srgb,var(--error)_80%,transparent)] text-[var(--text-inverse)]",
    warning: "bg-[color-mix(in_srgb,var(--warning)_80%,transparent)] text-[var(--text-inverse)]",
    info: "bg-[color-mix(in_srgb,var(--info)_80%,transparent)] text-[var(--text-inverse)]",
}

interface SnackbarProps {
    variant?: SnackbarVariant
    message: string
    duration?: number
    action?: {
        label: string
        onClick: () => void
    }
    onClose?: () => void
}

export const Snackbar = React.forwardRef<HTMLDivElement, SnackbarProps>(
    ({ variant = "default", message, duration = 3000, action, onClose }, ref) => {
        const [isVisible, setIsVisible] = React.useState(true)

        React.useEffect(() => {
            const timer = setTimeout(() => {
                setIsVisible(false)
                onClose?.()
            }, duration)

            return () => clearTimeout(timer)
        }, [duration, onClose])

        if (!isVisible) return null

        return (
            <div
                ref={ref}
                className={cn(snackbarBaseClassName, snackbarVariantClasses[variant])}
                role="alert"
            >
                <span className="text-sm font-medium flex-1">{message}</span>
                {action && (
                    <button
                        onClick={action.onClick}
                        className="text-xs font-semibold hover:opacity-80 transition-opacity"
                    >
                        {action.label}
                    </button>
                )}
                <button
                    onClick={() => setIsVisible(false)}
                    className="p-0 hover:opacity-80 transition-opacity"
                >
                    <X size={16} />
                </button>
            </div>
        )
    }
)

Snackbar.displayName = "Snackbar"
